import { Box, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { useIsEmbeddedPage } from "../layout/PageEmbedContext";

export type PageHeaderProps = {
  title?: ReactNode;
  /** Secondary line: single string or custom nodes (e.g. dashboard metadata). */
  subtitle?: ReactNode;
  /** Right-aligned primary actions (buttons, icon groups). */
  actions?: ReactNode;
  /**
   * Page name already lives in the dashboard navbar chrome.
   * Pass true only when this view needs its own distinct heading (rare).
   */
  showTitle?: boolean;
  titleVariant?: "h4" | "h5" | "h6";
  /** Bottom margin in theme spacing units. */
  mb?: number;
};

/**
 * Page chrome row under the navbar: optional subtitle + actions.
 * The page name is shown once in the navbar (icon + label).
 */
function PageHeader({
  title,
  subtitle,
  actions,
  showTitle = false,
  titleVariant = "h5",
  mb = 0,
}: PageHeaderProps) {
  const custom = useAppCustomStyles();
  const embedded = useIsEmbeddedPage();

  const hasTitle = showTitle && title != null && title !== false && title !== "";
  const hasSubtitle = subtitle != null && subtitle !== false;
  const hasActions = actions != null;

  // Inside a hub tab the hub already owns chrome; keep only actions.
  if (embedded) {
    return hasActions ? (
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb }}>
        <Box sx={custom.components.layout.pageHeaderActions}>{actions}</Box>
      </Box>
    ) : null;
  }

  if (!hasTitle && !hasSubtitle && !hasActions) {
    return null;
  }

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.25}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
      mb={mb}
      sx={{
        px: 0,
        py: 0,
        minWidth: 0,
      }}
    >
      {(hasTitle || hasSubtitle) && (
        <Box minWidth={0}>
          {hasTitle && (
            <Typography
              variant={titleVariant}
              component="h1"
              fontWeight={titleVariant === "h4" ? 700 : 650}
              color="text.primary"
            >
              {title}
            </Typography>
          )}
          {hasSubtitle && (
            <Box sx={custom.components.layout.pageHeaderSubtitle}>
              {typeof subtitle === "string" || typeof subtitle === "number" ? (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              ) : (
                subtitle
              )}
            </Box>
          )}
        </Box>
      )}
      {hasActions && (
        <Box
          sx={{
            ...custom.components.layout.pageHeaderActions,
            ...(hasTitle || hasSubtitle ? {} : { width: 1, justifyContent: { xs: "stretch", sm: "flex-end" } }),
          }}
        >
          {actions}
        </Box>
      )}
    </Stack>
  );
}

export default PageHeader;
